import React, { useEffect, useState } from 'react'
import { useText } from '../context/TextContext';
import { Form, useNavigate, useParams } from 'react-router-dom';
import { Article } from '../models/shared/Article';
import { FormControl, FormGroup, FormLabel, InputGroup, Spinner } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import { useForm } from 'react-hook-form';
import { AdvancedButton } from '../components/AdvancedButton';
import { useFirestore } from 'reactfire';
import { addDoc, collection, doc, setDoc } from 'firebase/firestore';
import { COLLECTIONS } from '../utils/shared/constants';
import { useOrganization } from '../context/OrganizationContext';
import { toast } from 'react-toastify';
import { useFirebaseDoc } from '../hooks/firebaseHooks';
import { calculatePriceWithVat, formatPrice } from '../utils/shared/priceCalculator';

interface Input {
    name: string;
    perUnitPrice: string;
    vatRate: string;
    rutTaxSubsidy: boolean;
    isDistanceBased: boolean;
    isActivity: boolean;
}

export default function ArticleWritePage() {
    const text = useText();
    const { orgId } = useOrganization();
    const { articleId } = useParams<{ articleId: string }>();
    const { register, handleSubmit, setValue, watch } = useForm<Input>({
        defaultValues: {
            vatRate: '25'
        }
    });
    const navigate = useNavigate();
    const [processing, setProcessing] = React.useState(false);
    const firestore = useFirestore();
    const [loading, setLoading] = useState(!!articleId);
    const [article] = useFirebaseDoc<Article>(`${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.ARTICLES}/${articleId}`);

    // Watch form values for display calculation
    const watchPerUnitPrice = watch('perUnitPrice');
    const watchVatRate = watch('vatRate');
    const watchIsActivity = watch('isActivity');

    // Calculate price with VAT for display only
    const priceWithVat = React.useMemo(() => {
        const priceWithoutVat = parseFloat(watchPerUnitPrice) || 0;
        const vatRate = parseFloat(watchVatRate) || 0;
        return formatPrice(calculatePriceWithVat(priceWithoutVat, vatRate));
    }, [watchPerUnitPrice, watchVatRate]);

    useEffect(() => {
        if (!article) {
            return;
        }
        setValue('name', article.name);
        setValue('perUnitPrice', article.perUnitPrice.toString());
        setValue('vatRate', article.vatRate.toString());
        setValue('rutTaxSubsidy', !!article.taxBenefit && article.taxBenefit.type === 'rut');
        setValue('isDistanceBased', !!article.isDistanceBased);
        setValue('isActivity', !!article.isActivity);

        setLoading(false);
    }, [article, setValue])

    const onSubmit = async (data: Input) => {
        setProcessing(true);

        const newArticle: Article = {
            name: data.name,
            perUnitPrice: parseFloat(data.perUnitPrice) || 0,
            vatRate: parseFloat(data.vatRate) || 0,
            status: 'active',
            isDistanceBased: data.isDistanceBased,
            isActivity: data.isActivity,
            ...(data.rutTaxSubsidy ? { taxBenefit: { type: 'rut', rate: 50 } } : {})
        }

        try {
            const colPath = `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.ARTICLES}`;
            if (articleId) {
                await setDoc(doc(firestore, colPath, articleId), newArticle, { merge: true });
            } else {
                await addDoc(collection(firestore, colPath), newArticle);
            }

            navigate(-1);
        } catch (error) {
            console.error(error);
            toast.error(text.defaultError);
        }

        setProcessing(false);
    }

    return (<>
        <PageHeader showBackButton title={articleId ? text.editArticle : text.newArticle} />

        {loading
            ? <Spinner />
            : <Form className='d-flex flex-column gap-4' onSubmit={handleSubmit(onSubmit)}>
                <FormGroup>
                    <FormLabel>{text.name}</FormLabel>
                    <FormControl {...register('name')} required />
                </FormGroup>

                <FormGroup>
                    <FormLabel>{text.vat} (%)</FormLabel>
                    <FormControl type='number' {...register('vatRate')} required />
                </FormGroup>

                <FormGroup>
                    <FormLabel>{watchIsActivity ? text.priceWithoutVatPerHour : text.priceWithoutVat}</FormLabel>
                    <InputGroup>
                        <FormControl
                            type='number'
                            step='0.01'
                            {...register('perUnitPrice')}
                            required
                        />
                        <InputGroup.Text>
                            {text.priceWithVat}: {priceWithVat}
                        </InputGroup.Text>
                    </InputGroup>
                </FormGroup>

                <FormGroup className="form-check">
                    <div className="d-flex align-items-center mb-2">
                        <FormControl
                            type="checkbox"
                            className="form-check-input me-2"
                            {...register('rutTaxSubsidy')}
                        />
                        <FormLabel htmlFor="rutTaxSubsidy" className="form-check-label mb-0">{text.rutTaxSubsidy}</FormLabel>
                    </div>
                    <div className="text-muted small">{text.rutDescription}</div>
                </FormGroup>

                <FormGroup className="form-check">
                    <div className="d-flex align-items-center mb-2">
                        <FormControl
                            type="checkbox"
                            className="form-check-input me-2"
                            {...register('isDistanceBased')}
                        />
                        <FormLabel htmlFor="isDistanceBased" className="form-check-label mb-0">{text.distanceBasedArticle}</FormLabel>
                    </div>
                    <div className="text-muted small">{text.distanceBasedDescription}</div>
                </FormGroup>

                <FormGroup className="form-check">
                    <div className="d-flex align-items-center mb-2">
                        <FormControl
                            type="checkbox"
                            className="form-check-input me-2"
                            {...register('isActivity')}
                        />
                        <FormLabel htmlFor="isActivity" className="form-check-label mb-0">{text.isActivityArticle}</FormLabel>
                    </div>
                    <div className="text-muted small">{text.isActivityArticleDescription}</div>
                </FormGroup>

                <div className='ms-auto d-flex gap-4'>
                    <AdvancedButton icon='bi-x' variant='secondary' onClick={() => navigate(-1)}>{text.cancel}</AdvancedButton>
                    <AdvancedButton processing={processing} type='submit' icon='bi-floppy'> {articleId ? text.save : text.create}</AdvancedButton>
                </div>
            </Form>
        }
    </>
    )
}
